import React, { useEffect, useState } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  Divider,
} from "@material-ui/core";

import { toolboxSolutions } from "../../../variables/toolboxSolutions";
import Button from "../../../components/CustomButtons/Button";
import PhantomCard from "./PhantomCard";
import ActualCard from "./ActualCard";
import { makeStyles } from "@material-ui/core/styles";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import { AttachFile, Delete, OpenInNew } from "@material-ui/icons";

import { updatePlanAPI } from "views/APIs/SolutionGeneratorUtility";
import NotificationContext from "views/Components/Context.js";

const useStyles = makeStyles((theme) => ({
  responseSection: {
    marginTop: theme.spacing(4),
    background:
      "linear-gradient(225deg, rgba(32, 103, 123, 1) 0%, rgba(32, 103, 123, 0.85) 60%, rgba(32, 103, 123, 0.7) 100%)",
    padding: theme.spacing(4),
    borderRadius: theme.spacing(3),
  },
  responseCard: {
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.12) 100%)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    color: "#ffffff",
  },
  responseTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: theme.spacing(1),
    letterSpacing: 0.1,
  },
  responseText: {
    color: "rgba(255, 255, 255, 0.95)",
    lineHeight: 1.6,
    marginBottom: theme.spacing(2),
  },
  stepperRoot: {
    background: "transparent",
    "& .MuiStepLabel-label": {
      color: "#fff",
      "&.MuiStepLabel-active": { color: "#fff" },
      "&.MuiStepLabel-completed": { color: "rgba(255,255,255,0.85)" },
    },
    "& .MuiStepIcon-root": {
      color: "rgba(255,255,255,0.35)",
      "&.MuiStepIcon-active": {
        color: "#20677b",
        border: "1px solid #fff",
        borderRadius: "50%",
      },
      "&.MuiStepIcon-completed": { color: "rgba(255,255,255,0.85)" },
    },
    "& .MuiStepContent-root": {
      borderLeft: "1px solid rgba(255,255,255,0.25)",
    },
  },
  stepTitle: {
    fontWeight: 600,
    color: "#ffffff",
  },
  stepDescription: {
    color: "rgba(255, 255, 255, 0.9)",
    lineHeight: 1.5,
  },
}));

export function ResponseViewer({ response, loading, setResponse }) {
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);
  const [activeStep, setActiveStep] = React.useState(0);
  const [vectorDbFiles, setVectorDbFiles] = useState(
    response.reference_files?.vector_db_files || []
  );
  const [userUploadedFiles, setUserUploadedFiles] = useState(
    response.reference_files?.user_uploaded_files || []
  );
  const [lockNextSteps, setLockNextSteps] = useState(true);

  const handleNext = () => {
    setActiveStep((prev) =>
      Math.min(prev + 1, (response.plan?.steps?.length ?? 1) - 1)
    );
  };
  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    // set the active step as the first step which does nto have the _id field
    setActiveStep(response.plan?.steps?.findIndex((step) => !step._id));
    if (typeof response.plan === "object" && response.plan?.steps[0]?._id) {
      setLockNextSteps(false);
    }
  }, [response]);

  const [dots, setDots] = useState("");

  useEffect(() => {
    if (!response || loading) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [response, loading]);

  const updatePlan = async (plan) => {
    const formDataforPlan = new FormData();
    const project_id = plan.steps[0]._id;
    formDataforPlan.append("plan", JSON.stringify(plan));
    formDataforPlan.append("project_id", project_id);
    await updatePlanAPI(formDataforPlan);
  };

  console.log("RESPONSE VIEWER RESPONSE:", response);

  if (response.error) {
    return (
      <Box>
        <div className={classes.responseSection}>
          <Typography variant="body1" className={classes.responseText}>
            {response.plan}
          </Typography>
        </div>
      </Box>
    );
  }

  if (!response || loading) {
    return (
      <>
        <Box>
          <div className={classes.responseSection}>
            <Typography variant="body1" className={classes.responseText}>
              Fetching your plan{dots}
            </Typography>
          </div>
        </Box>
      </>
    );
  }

  return (
    <>
      {/* response */}
      {response && !loading && (
        <Box>
          <div className={classes.responseSection}>
            {/* Solution Overview */}
            <Box>
              <>
                <Typography variant="h5" className={classes.responseTitle}>
                  {response.plan.solution_title}
                </Typography>
                <Typography variant="body1" className={classes.responseText}>
                  {response.plan.solution_description}
                </Typography>
              </>
            </Box>

            {/* Why GenAI is Important */}
            <Box>
              <Typography variant="body1" className={classes.responseText}>
                {response.plan.solution_context}
              </Typography>
            </Box>

            {/* Reference Files */}
            <Box>
              <Typography variant="body1" className={classes.responseText}>
                {vectorDbFiles?.length > 0 && (
                  <>
                    <Typography variant="h7" className={classes.responseText}>
                      Relevant files for additional context:
                    </Typography>
                    <Typography
                      variant="body1"
                      className={classes.responseText}
                    >
                      {vectorDbFiles?.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.filename}
                          onDelete={() =>
                            setVectorDbFiles(
                              vectorDbFiles.filter((_, i) => i !== index)
                            )
                          }
                          onClick={() => window.open(file.s3_url, "_blank")}
                          icon={<AttachFile fontSize="small" />}
                          style={{
                            marginRight: "4px",
                            cursor: "pointer",
                          }}
                        />
                      ))}
                    </Typography>
                  </>
                )}
                {userUploadedFiles?.length > 0 && (
                  <>
                    <Typography variant="h7" className={classes.responseTitle}>
                      User uploaded files
                    </Typography>
                    <Typography
                      variant="body1"
                      className={classes.responseText}
                    >
                      {userUploadedFiles?.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.filename}
                          icon={<AttachFile fontSize="small" />}
                          onDelete={() =>
                            setUserUploadedFiles(
                              userUploadedFiles.filter((_, i) => i !== index)
                            )
                          }
                          onClick={() => window.open(file.s3_url, "_blank")}
                          style={{
                            marginRight: "4px",
                            cursor: "pointer",
                          }}
                        />
                      ))}
                    </Typography>
                  </>
                )}
              </Typography>
            </Box>

            {/* Implementation Steps */}
            {response?.plan?.steps?.length > 0 && (
              <Box>
                <>
                  <Typography variant="h7" className={classes.responseTitle}>
                    Here is your plan:
                  </Typography>

                  <Stepper
                    orientation="vertical"
                    className={classes.stepperRoot}
                    activeStep={activeStep}
                  >
                    {response?.plan?.steps?.map((step, index) => {
                      const isCreated = step._id && step._id !== "None";

                      return (
                        <Step
                          key={index}
                          color="quCreateColor"
                          disabled={index !== 0}
                        >
                          <StepLabel>
                            <Typography className={classes.stepTitle}>
                              {step.step_title}
                            </Typography>
                          </StepLabel>

                          <StepContent>
                            <Typography
                              className={classes.stepDescription}
                              style={{ marginBottom: 12 }}
                            >
                              {step.step_description}
                            </Typography>

                            {/* Card area: phantom vs real */}
                            {!isCreated && step.toolbox_type !== "None" && (
                              <PhantomCard
                                title={step.toolbox_name}
                                description={step.toolbox_description}
                                toolbox={step.toolbox_type}
                                index={index}
                                toolboxType={step.toolbox_type}
                                setTitleDescriptionForStep={(
                                  title,
                                  description,
                                  id
                                ) => {
                                  setResponse((prev) => ({
                                    ...prev,
                                    plan: {
                                      ...prev.plan,
                                      steps: prev.plan.steps.map((st, i) =>
                                        i === index
                                          ? {
                                              ...st,
                                              _id: id,
                                              toolbox_name: title,
                                              toolbox_description: description,
                                            }
                                          : st
                                      ),
                                    },
                                  }));
                                  updatePlan({
                                    ...response,
                                    plan: {
                                      ...response.plan,
                                      steps: response.plan.steps.map((st, i) =>
                                        i === index
                                          ? {
                                              ...st,
                                              _id: id,
                                              toolbox_name: title,
                                              toolbox_description: description,
                                            }
                                          : st
                                      ),
                                    },
                                  });
                                }}
                                plan={response}
                                isLocked={index > 0 && lockNextSteps}
                              />
                            )}

                            {isCreated && step.toolbox_type !== "NONE" && (
                              <ActualCard
                                title={step.toolbox_name}
                                description={step.toolbox_description}
                                toolboxType={step.toolbox_type}
                                id={step._id}
                              />
                            )}

                            {/* Prev / Next controls */}
                            <div style={{ marginTop: 8 }}>
                              <Button
                                onClick={handleBack}
                                disabled={activeStep === 0}
                                style={{ marginRight: 8 }}
                                color="secondary"
                              >
                                Prev
                              </Button>

                              <Button
                                variant="outlined"
                                color="quCreateColor"
                                onClick={handleNext}
                                disabled={
                                  activeStep ===
                                  response?.plan?.steps?.length - 1
                                }
                              >
                                Next
                              </Button>
                            </div>
                          </StepContent>
                        </Step>
                      );
                    })}
                  </Stepper>
                </>
              </Box>
            )}

            {/* Recommended Toolboxes */}
            {response?.upsell?.length > 0 && (
              <Box>
                <>
                  <Typography variant="h7" className={classes.responseTitle}>
                    You can additionally do the following by purchasing these
                    toolboxes:
                  </Typography>

                  <Grid container spacing={2} style={{ marginTop: 8 }}>
                    {(Array.isArray(response.plan.upsell)
                      ? response.plan.upsell
                      : [response.plan.upsell]
                    ).map((u, i) => (
                      <Grid key={i} item xs={12} sm={6} md={4}>
                        <Card
                          elevation={2}
                          style={{ borderRadius: 12, height: "100%" }}
                        >
                          <CardContent>
                            <Typography
                              variant="subtitle1"
                              style={{ marginBottom: 6 }}
                            >
                              {u.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{ marginBottom: 10, opacity: 0.9 }}
                            >
                              {u.description}
                            </Typography>
                            <Button variant="contained" color="quCreateColor">
                              Purchase
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              </Box>
            )}
          </div>
        </Box>
      )}
    </>
  );
}
